import {
  dispatchExternalWebAdminAuthBoundary,
  dispatchWebAdminAuthError,
  type WebAdminAuthBoundaryKind,
} from '@/lib/auth-events';

import { ApiError } from './errors';

export interface AuthSessionSnapshot {
  readonly generation: number;
  readonly failure: ApiError | null;
  readonly sharedEpoch: number;
  readonly sharedEpochNonce: string | null;
}

let sessionAuthFailure: ApiError | null = null;
let authSessionGeneration = 0;
let localAuthTransitionActive = false;
let authBoundaryChannel: BroadcastChannel | null = null;
let removeStorageListener: (() => void) | null = null;
let authBoundarySyncInitialized = false;
let lastAppliedAuthBoundary: AuthBoundaryMessage | null = null;

const AUTH_BOUNDARY_CHANNEL_NAME = 'erp24:web-admin-auth-boundary';
const AUTH_BOUNDARY_STORAGE_KEY = 'erp24:web-admin-auth-boundary-event';
const AUTH_SESSION_LOCK_NAME = 'erp24:web-admin-auth-endpoint';

interface AuthBoundaryMessage {
  readonly kind: WebAdminAuthBoundaryKind;
  readonly epoch: number;
  readonly nonce: string;
}

function authRequiredError(): ApiError {
  return new ApiError(
    401,
    'web_admin_auth_required',
    'Требуется авторизация в панели администратора',
  );
}

function authTransitionError(): ApiError {
  return new ApiError(
    409,
    'auth_session_transition',
    'Сессия авторизации меняется. Повторите действие после входа.',
  );
}

function isAuthBoundaryKind(value: unknown): value is WebAdminAuthBoundaryKind {
  return value === 'transition' || value === 'login' || value === 'logout';
}

function applyAuthBoundary(
  kind: WebAdminAuthBoundaryKind,
  options: { preserveLocalTransition?: boolean } = {},
): void {
  authSessionGeneration += 1;
  if (options.preserveLocalTransition && localAuthTransitionActive) {
    sessionAuthFailure = authTransitionError();
    return;
  }
  if (kind === 'login') {
    sessionAuthFailure = null;
  } else if (kind === 'logout') {
    sessionAuthFailure = authRequiredError();
  } else {
    sessionAuthFailure = authTransitionError();
  }
}

function parseAuthBoundaryMessage(value: unknown): AuthBoundaryMessage | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<AuthBoundaryMessage>;
  if (
    !isAuthBoundaryKind(candidate.kind) ||
    !Number.isSafeInteger(candidate.epoch) ||
    (candidate.epoch ?? 0) <= 0 ||
    typeof candidate.nonce !== 'string'
  ) {
    return null;
  }
  return { kind: candidate.kind, epoch: candidate.epoch as number, nonce: candidate.nonce };
}

function readPersistedAuthBoundary(): AuthBoundaryMessage | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(AUTH_BOUNDARY_STORAGE_KEY);
    return value ? parseAuthBoundaryMessage(JSON.parse(value) as unknown) : null;
  } catch {
    return null;
  }
}

function isSameAuthBoundary(left: AuthBoundaryMessage, right: AuthBoundaryMessage): boolean {
  return left.epoch === right.epoch && left.nonce === right.nonce && left.kind === right.kind;
}

function getCurrentSharedAuthBoundary(): AuthBoundaryMessage | null {
  // localStorage is one shared last-writer register. When it is available, the
  // current record is stronger evidence than an asynchronously delivered
  // BroadcastChannel message or an older in-memory observation. Numeric epochs
  // can tie when Web Locks are unavailable, so nonce ordering is never used as
  // a substitute for actual write order.
  return readPersistedAuthBoundary() ?? lastAppliedAuthBoundary;
}

/**
 * Serialize cookie-changing endpoints and protected-response session failures
 * in one cross-tab critical section. The durable epoch remains necessary
 * because channel delivery is asynchronous and Web Locks may be unavailable.
 */
export async function runWithSharedAuthSessionLock<T>(
  operation: () => Promise<T> | T,
  signal?: AbortSignal | null,
): Promise<T> {
  const lockManager =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as Omit<Navigator, 'locks'> & { locks?: LockManager }).locks;
  if (!lockManager) {
    return await operation();
  }

  const lockOptions: LockOptions = { mode: 'exclusive' };
  if (signal) {
    lockOptions.signal = signal;
  }
  return await lockManager.request(AUTH_SESSION_LOCK_NAME, lockOptions, operation);
}

function receiveExternalAuthBoundary(value: unknown): void {
  const message = parseAuthBoundaryMessage(value);
  if (!message) return;

  const persisted = readPersistedAuthBoundary();
  // Broadcast/storage delivery is asynchronous. When durable storage exists,
  // only its current last-writer record may affect this realm. This also handles
  // equal numeric epochs correctly: the record that actually won the storage
  // write wins, regardless of its random nonce.
  if (persisted && !isSameAuthBoundary(message, persisted)) return;
  if (lastAppliedAuthBoundary && isSameAuthBoundary(message, lastAppliedAuthBoundary)) {
    return;
  }

  if (!persisted && lastAppliedAuthBoundary && message.epoch <= lastAppliedAuthBoundary.epoch) {
    // With neither durable storage nor Web Lock serialization, two realms can
    // publish boundaries from clocks that are not globally monotonic. A
    // different equal/lower epoch may still be a causally later boundary from
    // a realm that missed earlier broadcasts. Treat every such conflict as
    // ambiguous and fail closed; the bridge will reconcile the authoritative
    // cookie through `/auth/me`.
    lastAppliedAuthBoundary = message;
    applyAuthBoundary('transition', { preserveLocalTransition: true });
    dispatchExternalWebAdminAuthBoundary('transition');
    return;
  }

  lastAppliedAuthBoundary = message;
  applyAuthBoundary(message.kind, { preserveLocalTransition: true });
  dispatchExternalWebAdminAuthBoundary(message.kind);
}

function synchronizePersistedAuthBoundary(): void {
  const persisted = readPersistedAuthBoundary();
  if (persisted) {
    receiveExternalAuthBoundary(persisted);
  }
}

/**
 * Keep the in-memory session generation aligned with the same-origin HttpOnly
 * cookie, which is shared by every browser tab. BroadcastChannel is preferred;
 * the storage event is a compatibility fallback for older browser runtimes.
 */
export function ensureAuthSessionBoundarySync(): void {
  if (typeof window === 'undefined' || authBoundarySyncInitialized) return;
  authBoundarySyncInitialized = true;

  if (typeof BroadcastChannel !== 'undefined') {
    authBoundaryChannel = new BroadcastChannel(AUTH_BOUNDARY_CHANNEL_NAME);
    authBoundaryChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
      receiveExternalAuthBoundary(event.data);
    });
  }

  const storageListener = (event: StorageEvent) => {
    if (event.key !== AUTH_BOUNDARY_STORAGE_KEY || !event.newValue) return;
    try {
      receiveExternalAuthBoundary(JSON.parse(event.newValue) as unknown);
    } catch {
      // Ignore malformed cross-tab messages; the server cookie stays authoritative.
    }
  };
  window.addEventListener('storage', storageListener);
  removeStorageListener = () => window.removeEventListener('storage', storageListener);
  synchronizePersistedAuthBoundary();
}

function publishAuthBoundary(kind: WebAdminAuthBoundaryKind): void {
  if (typeof window === 'undefined') return;
  ensureAuthSessionBoundarySync();
  const currentBoundary = getCurrentSharedAuthBoundary();
  const message: AuthBoundaryMessage = {
    kind,
    epoch: Math.max(Date.now() * 1_000, (currentBoundary?.epoch ?? 0) + 1),
    nonce: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };
  try {
    // Persist even when BroadcastChannel exists. Protected response completion
    // can read this synchronously instead of waiting for an async message.
    window.localStorage.setItem(AUTH_BOUNDARY_STORAGE_KEY, JSON.stringify(message));
  } catch {
    // BroadcastChannel remains the fallback when durable storage is disabled.
  }
  lastAppliedAuthBoundary = message;
  authBoundaryChannel?.postMessage(message);
}

function staleSessionResponseError(): ApiError {
  return new ApiError(
    409,
    'stale_auth_session_response',
    'Ответ относится к предыдущей сессии и был отброшен.',
  );
}

export function captureAuthSessionSnapshot(): AuthSessionSnapshot {
  synchronizePersistedAuthBoundary();
  const sharedBoundary = getCurrentSharedAuthBoundary();
  return {
    generation: authSessionGeneration,
    failure: sessionAuthFailure,
    sharedEpoch: sharedBoundary?.epoch ?? 0,
    sharedEpochNonce: sharedBoundary?.nonce ?? null,
  };
}

export function getSessionAuthFailure(): ApiError | null {
  synchronizePersistedAuthBoundary();
  return sessionAuthFailure;
}

export function assertAuthSessionAvailable(): void {
  synchronizePersistedAuthBoundary();
  if (sessionAuthFailure) {
    throw sessionAuthFailure;
  }
}

/**
 * Reject protected responses that crossed a login/logout/session-failure
 * boundary while they were in flight. Comparing both generation and failure
 * identity also prevents a successful sibling response from repopulating the
 * cache after another request has invalidated the current session.
 */
export function assertResponseBelongsToAuthSession(requestSnapshot: AuthSessionSnapshot): void {
  synchronizePersistedAuthBoundary();
  const sharedBoundary = getCurrentSharedAuthBoundary();
  if (
    requestSnapshot.generation !== authSessionGeneration ||
    requestSnapshot.failure !== sessionAuthFailure ||
    requestSnapshot.sharedEpoch !== (sharedBoundary?.epoch ?? 0) ||
    requestSnapshot.sharedEpochNonce !== (sharedBoundary?.nonce ?? null)
  ) {
    throw staleSessionResponseError();
  }
}

/**
 * Verify that an async failure-registration call still owns the active
 * fail-closed barrier before its transport exposes the original 401. The
 * request snapshot itself is intentionally stale after a successful commit,
 * so identity of the committed structured error is the precise invariant.
 */
export function assertSessionAuthFailureCurrent(apiError: ApiError): void {
  synchronizePersistedAuthBoundary();
  if (sessionAuthFailure !== apiError) {
    throw staleSessionResponseError();
  }
}

/**
 * Register a transport-observed session failure only when it belongs to the
 * current cookie session. Returns false for a stale response from an older
 * generation.
 */
function commitSessionAuthFailure(
  apiError: ApiError,
  requestSnapshot: AuthSessionSnapshot,
): ApiError | null {
  synchronizePersistedAuthBoundary();
  const sharedBoundary = getCurrentSharedAuthBoundary();
  if (
    requestSnapshot.generation !== authSessionGeneration ||
    requestSnapshot.sharedEpoch !== (sharedBoundary?.epoch ?? 0) ||
    requestSnapshot.sharedEpochNonce !== (sharedBoundary?.nonce ?? null)
  ) {
    return null;
  }

  if (sessionAuthFailure !== null) {
    // Protected work cannot start behind a failure, but `/auth/me` can. Its
    // authoritative 401 belongs to the current barrier only when the request
    // started behind that exact barrier. A sibling request that started before
    // the first 401 instead crossed a failure boundary and must be stale.
    return requestSnapshot.failure === sessionAuthFailure ? sessionAuthFailure : null;
  }

  sessionAuthFailure = apiError;

  try {
    dispatchWebAdminAuthError({
      statusCode: apiError.statusCode,
      error: apiError.error,
      ...(apiError.requestId ? { requestId: apiError.requestId } : {}),
    });
  } catch {
    // Auth state propagation must never block API error propagation.
  }
  // The HttpOnly cookie is shared by every tab. Keep the exact server error in
  // this realm for diagnostics, while closing peer realms with the existing
  // logout-equivalent boundary so they clear principal-scoped cache and I/O.
  publishAuthBoundary('logout');
  return apiError;
}

export async function registerSessionAuthFailure(
  apiError: ApiError,
  requestSnapshot: AuthSessionSnapshot,
  options: { sharedLockHeld?: boolean } = {},
): Promise<ApiError | null> {
  if (options.sharedLockHeld) {
    return commitSessionAuthFailure(apiError, requestSnapshot);
  }
  return runWithSharedAuthSessionLock(() => commitSessionAuthFailure(apiError, requestSnapshot));
}

export function recordAuthTransitionStarted(): void {
  localAuthTransitionActive = true;
  applyAuthBoundary('transition');
  publishAuthBoundary('transition');
}

/** Re-announce a queued transition after it acquires the cross-tab auth lock. */
export function publishAuthTransitionInProgress(): void {
  publishAuthBoundary('transition');
}

export function recordLoginSuccess(): void {
  localAuthTransitionActive = false;
  applyAuthBoundary('login');
  publishAuthBoundary('login');
}

/**
 * A successful logout is an explicit session boundary. Close protected I/O
 * locally before the auth store finishes navigating away, without dispatching
 * a duplicate auth-error event.
 */
export function recordLogoutSuccess(): void {
  localAuthTransitionActive = false;
  applyAuthBoundary('logout');
  publishAuthBoundary('logout');
}

export function recordCurrentSessionRecovery(requestStart: AuthSessionSnapshot): void {
  synchronizePersistedAuthBoundary();
  const sharedBoundary = getCurrentSharedAuthBoundary();
  if (
    requestStart.failure !== null &&
    sessionAuthFailure === requestStart.failure &&
    requestStart.generation === authSessionGeneration &&
    requestStart.sharedEpoch === (sharedBoundary?.epoch ?? 0) &&
    requestStart.sharedEpochNonce === (sharedBoundary?.nonce ?? null)
  ) {
    applyAuthBoundary('login');
    publishAuthBoundary('login');
  }
}

export function __resetAuthSessionBarrierForTests(): void {
  authBoundaryChannel?.close();
  authBoundaryChannel = null;
  removeStorageListener?.();
  removeStorageListener = null;
  authBoundarySyncInitialized = false;
  lastAppliedAuthBoundary = null;
  sessionAuthFailure = null;
  authSessionGeneration = 0;
  localAuthTransitionActive = false;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(AUTH_BOUNDARY_STORAGE_KEY);
    } catch {
      // Test cleanup stays best-effort in storage-restricted DOM environments.
    }
  }
}
